import React from "react";
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Music2 } from "lucide-react";

/**
 * InstagramReelFrame — wraps a vertical 9:16 video to look like it's playing
 * inside a phone on Instagram Reels.
 *
 * Used as the rendering template for any ad with format: "reel".
 *
 * Props:
 *   - src       (string)  : video URL
 *   - poster    (string)  : poster image URL
 *   - handle    (string)  : Instagram handle to show (e.g. "@anneflinn")
 *   - caption   (string)  : caption shown below the handle
 *   - testId    (string)  : data-testid prefix for the video element
 */
const InstagramReelFrame = ({ src, poster, handle = "@anneflinn", caption, testId }) => {
  return (
    <div
      className="relative mx-auto rounded-[1.7rem] p-[1.5px] shadow-2xl"
      style={{
        width: 240,
        background:
          "linear-gradient(180deg, #d6d6d8 0%, #b4b4b8 50%, #8e8e93 100%)",
      }}
      data-testid="instagram-reel-frame"
    >
      {/* Subtle glow halo behind the phone */}
      <div
        aria-hidden="true"
        className="absolute -inset-3 rounded-[2rem] bg-[var(--af-lime)]/15 blur-2xl opacity-50 -z-10"
      />
      <div
        className="relative rounded-[1.6rem] overflow-hidden"
        style={{
          aspectRatio: "9/17",
          backgroundColor: "var(--af-cream)",
        }}
      >
        {/* Status notch — silver gradient matching the bezel */}
        <div
          className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full z-30"
          style={{
            background:
              "linear-gradient(180deg, #8e8e93 0%, #5a5a5e 60%, #2c2c2e 100%)",
          }}
        />

        {/* Video fills the screen */}
        <video
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={testId}
        />

        {/* Top gradient + IG header bar — cream/light with dark text */}
        <div className="absolute top-0 left-0 right-0 pt-7 pb-3 px-3 z-20 bg-gradient-to-b from-[var(--af-cream)] via-[var(--af-cream)]/40 to-transparent flex items-center justify-between text-[var(--af-navy-deep)] text-[11px]">
          <span className="font-semibold tracking-wide">Reels</span>
          <MoreHorizontal size={14} />
        </div>

        {/* Right-side action rail */}
        <div className="absolute right-2 bottom-20 z-20 flex flex-col items-center gap-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
          <div className="flex flex-col items-center gap-0.5">
            <Heart size={22} strokeWidth={2} />
            <span className="text-[9px] font-medium">2.1k</span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <MessageCircle size={22} strokeWidth={2} />
            <span className="text-[9px] font-medium">184</span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <Send size={22} strokeWidth={2} />
            <span className="text-[9px] font-medium">Send</span>
          </div>
          <Bookmark size={22} strokeWidth={2} />
        </div>

        {/* Bottom caption + handle — no gradient, text sits directly over the video */}
        <div className="absolute bottom-0 left-0 right-0 z-20 pb-3 px-3 pr-12 pt-2 text-[var(--af-cream)] drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--af-lime)] via-[var(--af-lime-soft)] to-[var(--af-navy)] p-[1.5px]">
              <div className="w-full h-full rounded-full bg-[var(--af-navy-deep)]" />
            </div>
            <span className="text-[11px] font-semibold">{handle}</span>
            <span className="text-[10px] border border-[var(--af-cream)]/80 rounded px-1.5 py-px font-medium">
              Follow
            </span>
          </div>
          {caption && (
            <div className="text-[10px] leading-snug text-[var(--af-cream)]/95 line-clamp-2">
              {caption}
            </div>
          )}
          <div className="flex items-center gap-1 mt-1.5 text-[9px] text-[var(--af-cream)]/90">
            <Music2 size={10} />
            <span className="truncate">Original audio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstagramReelFrame;
export { InstagramReelFrame };
